import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { LayoutStyleProvider } from "@/contexts/LayoutStyleContext";
import { RequireAuth } from "@/auth/RequireAuth";
import { queryClient } from "@/api/queryClient";
import ConsultaTipoArquivo from "@/features/tipo-arquivo/ConsultaTipoArquivo";
import ConsultaUsuario from "@/features/usuario/ConsultaUsuario";
import FormUsuario from "@/features/usuario/FormUsuario";
import ConsultaMensageria from "@/features/mensageria/ConsultaMensageria";
import ConsultaFranquia from "@/features/franquia/ConsultaFranquia";
import HealthScore from "@/features/health-score/HealthScore";
import FrequenciaAcessos from "@/features/acessos/FrequenciaAcessos";
import DetalheAcessosInstituicao from "@/features/acessos/DetalheAcessosInstituicao";
import ConsultaLayoutNotificacao from "@/features/layout-notificacao/ConsultaLayoutNotificacao";
import FormLayoutNotificacao from "@/features/layout-notificacao/FormLayoutNotificacao";
import FormFranquia from "@/features/franquia/FormFranquia";
import ExportacaoBackupCsv from "@/features/relatorios/ExportacaoBackupCsv";
import ImportacaoIdoso from "@/features/importacao/ImportacaoIdoso";
import ImportacaoProfissional from "@/features/importacao/ImportacaoProfissional";
import ConsultaCobranca from "@/features/cobranca/ConsultaCobranca";
import ConsultaInstituicaoInteracao from "@/features/instituicao-interacao/ConsultaInstituicaoInteracao";
import ConsultaInstituicao from "@/features/instituicao/ConsultaInstituicao";
import FormInstituicao from "@/features/instituicao/FormInstituicao";
import FichaInstituicao from "@/features/instituicao/FichaInstituicao";
import FormConfiguracaoInstituicao from "@/features/instituicao/FormConfiguracaoInstituicao";
import DashboardSilpiFarma from "@/features/silpi-farma/DashboardSilpiFarma";
import DashboardTv from "@/features/silpi-farma/DashboardTv";
import ConsultaNotificacao from "@/features/notificacao/ConsultaNotificacao";
import Overview from "@/features/overview/Overview";
import OverviewTv from "@/features/overview/OverviewTv";
import AgenteChat from "@/features/agente/AgenteChat";

// Bookmarks do período de coexistência (/v2/Usuario/Consulta) perdem o prefixo;
// o nginx já responde 301 para eles, isto cobre o caso de ele ainda não ter sido
// atualizado. Qualquer outra URL desconhecida vai para a Home, como fazia o
// otherwise() do ui-router.
function RotaDesconhecida() {
  const { pathname, search, hash } = useLocation();
  const semPrefixo = pathname.replace(/^\/v2(?=\/|$)/i, "");
  const destino = semPrefixo !== pathname ? `${semPrefixo || "/"}${search}${hash}` : "/";
  return <Navigate to={destino} replace />;
}

// AppLayout ainda recebe title/breadcrumbs por rota (repetido a cada <Route>) —
// com 5 rotas reais ainda é tolerável; migrar pra `handle` de cada <Route> +
// useMatches() exige trocar pra API de data router do react-router (mudança
// maior). Revisitar na próxima leva de rotas, quando a repetição doer mais.
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <LayoutStyleProvider>
          <TooltipProvider delayDuration={0}>
            <Toaster richColors position="top-right" />
            <RequireAuth>
              <Routes>
                <Route
                  path="/"
                  element={
                    <AppLayout title="Painel Admin" breadcrumbs={[{ label: "Início" }]}>
                      <Overview />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Overview/Consulta"
                  element={
                    <AppLayout title="Overview" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Overview" }]}>
                      <Overview />
                    </AppLayout>
                  }
                />
                <Route
                  path="/TipoArquivo/Consulta"
                  element={
                    <AppLayout
                      title="Consulta de Tipo de Arquivos"
                      breadcrumbs={[{ label: "Início", href: "/" }, { label: "Tipo de Arquivo" }]}
                    >
                      <ConsultaTipoArquivo />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Usuario/Consulta"
                  element={
                    <AppLayout title="Usuários" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Usuários" }]}>
                      <ConsultaUsuario />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Usuario/Form"
                  element={
                    <AppLayout
                      title="Cadastro de Usuário"
                      breadcrumbs={[{ label: "Início", href: "/" }, { label: "Usuários", href: "/Usuario/Consulta" }, { label: "Novo" }]}
                    >
                      <FormUsuario />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Usuario/Form/:id"
                  element={
                    <AppLayout
                      title="Cadastro de Usuário"
                      breadcrumbs={[{ label: "Início", href: "/" }, { label: "Usuários", href: "/Usuario/Consulta" }, { label: "Editar" }]}
                    >
                      <FormUsuario />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Mensageria/Consulta"
                  element={
                    <AppLayout title="Mensageria" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Mensageria" }]}>
                      <ConsultaMensageria />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Franquia/Consulta"
                  element={
                    <AppLayout title="Franquias" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Franquias" }]}>
                      <ConsultaFranquia />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Instituicao/HealthScore"
                  element={
                    <AppLayout title="Health Score" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Health Score" }]}>
                      <HealthScore />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Acessos/Frequencia"
                  element={
                    <AppLayout title="Frequência de acessos" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Frequência de acessos" }]}>
                      <FrequenciaAcessos />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Acessos/Instituicao/:idInstituicao"
                  element={
                    <AppLayout
                      title="Acessos da instituição"
                      breadcrumbs={[
                        { label: "Início", href: "/" },
                        { label: "Frequência de acessos", href: "/Acessos/Frequencia" },
                        { label: "Instituição" },
                      ]}
                    >
                      <DetalheAcessosInstituicao />
                    </AppLayout>
                  }
                />
                <Route
                  path="/LayoutNotificacao/Consulta"
                  element={
                    <AppLayout
                      title="Layouts de Notificação"
                      breadcrumbs={[{ label: "Início", href: "/" }, { label: "Layouts de Notificação" }]}
                    >
                      <ConsultaLayoutNotificacao />
                    </AppLayout>
                  }
                />
                <Route
                  path="/LayoutNotificacao/Form"
                  element={
                    <AppLayout
                      title="Cadastro de Layout de Notificação"
                      breadcrumbs={[
                        { label: "Início", href: "/" },
                        { label: "Layouts de Notificação", href: "/LayoutNotificacao/Consulta" },
                        { label: "Novo" },
                      ]}
                    >
                      <FormLayoutNotificacao />
                    </AppLayout>
                  }
                />
                <Route
                  path="/LayoutNotificacao/Form/:idLayout"
                  element={
                    <AppLayout
                      title="Cadastro de Layout de Notificação"
                      breadcrumbs={[
                        { label: "Início", href: "/" },
                        { label: "Layouts de Notificação", href: "/LayoutNotificacao/Consulta" },
                        { label: "Editar" },
                      ]}
                    >
                      <FormLayoutNotificacao />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Franquia/Adicionar"
                  element={
                    <AppLayout
                      title="Cadastro de Franquia"
                      breadcrumbs={[{ label: "Início", href: "/" }, { label: "Franquias", href: "/Franquia/Consulta" }, { label: "Novo" }]}
                    >
                      <FormFranquia />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Relatorios/ExportacaoBackupCsv"
                  element={
                    <AppLayout
                      title="Exportação de Backup CSV"
                      breadcrumbs={[{ label: "Início", href: "/" }, { label: "Exportação de Backup CSV" }]}
                    >
                      <ExportacaoBackupCsv />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Importacao/Idoso"
                  element={
                    <AppLayout title="Importação de Idosos" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Importação de Idosos" }]}>
                      <ImportacaoIdoso />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Importacao/Profissional"
                  element={
                    <AppLayout title="Importação de Profissionais" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Importação de Profissionais" }]}>
                      <ImportacaoProfissional />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Cobranca/Consulta"
                  element={
                    <AppLayout title="Cobranças" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Cobranças" }]}>
                      <ConsultaCobranca />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Cobranca/Consulta/:filtroId"
                  element={
                    <AppLayout title="Cobranças" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Cobranças" }]}>
                      <ConsultaCobranca />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Cobranca/Consulta/Instituicao/:instituicaoId"
                  element={
                    <AppLayout title="Cobranças" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Cobranças" }]}>
                      <ConsultaCobranca />
                    </AppLayout>
                  }
                />
                <Route
                  path="/InstituicaoInteracao/Consulta"
                  element={
                    <AppLayout title="Interações" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Interações" }]}>
                      <ConsultaInstituicaoInteracao />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Instituicao/Consulta"
                  element={
                    <AppLayout title="Instituições" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Instituições" }]}>
                      <ConsultaInstituicao />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Instituicao/Form"
                  element={
                    <AppLayout
                      title="Cadastro de Instituição"
                      breadcrumbs={[{ label: "Início", href: "/" }, { label: "Instituições", href: "/Instituicao/Consulta" }, { label: "Novo" }]}
                    >
                      <FormInstituicao />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Instituicao/Form/:idInstituicao"
                  element={
                    <AppLayout
                      title="Cadastro de Instituição"
                      breadcrumbs={[{ label: "Início", href: "/" }, { label: "Instituições", href: "/Instituicao/Consulta" }, { label: "Editar" }]}
                    >
                      <FormInstituicao />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Instituicao/Ficha/:idInstituicao"
                  element={
                    <AppLayout
                      title="Ficha da Instituição"
                      breadcrumbs={[{ label: "Início", href: "/" }, { label: "Instituições", href: "/Instituicao/Consulta" }, { label: "Ficha" }]}
                    >
                      <FichaInstituicao />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Instituicao/Configuracao/:idInstituicao"
                  element={
                    <AppLayout
                      title="Configurações da Instituição"
                      breadcrumbs={[{ label: "Início", href: "/" }, { label: "Instituições", href: "/Instituicao/Consulta" }, { label: "Configurações" }]}
                    >
                      <FormConfiguracaoInstituicao />
                    </AppLayout>
                  }
                />
                {/* Sem item de menu, como no legado: acesso só pela URL. */}
                <Route
                  path="/Agente/Chat"
                  element={
                    <AppLayout title="Agente SILPI" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Agente" }]}>
                      <AgenteChat />
                    </AppLayout>
                  }
                />
                <Route
                  path="/Notificacao/Consulta"
                  element={
                    <AppLayout title="Notificações" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Notificações" }]}>
                      <ConsultaNotificacao />
                    </AppLayout>
                  }
                />
                <Route
                  path="/SilpiFarma/Dashboard"
                  element={
                    <AppLayout title="Silpi Farma" breadcrumbs={[{ label: "Início", href: "/" }, { label: "Silpi Farma" }]}>
                      <DashboardSilpiFarma />
                    </AppLayout>
                  }
                />
                {/* Modo TV: fora do AppLayout de propósito (sem menu/top bar), ainda dentro do RequireAuth. */}
                <Route path="/SilpiFarma/Dashboard/TV" element={<DashboardTv />} />
                {/* Modo TV da Home: Overview + slides do Silpi Farma. */}
                <Route path="/TV" element={<OverviewTv />} />
                <Route path="*" element={<RotaDesconhecida />} />
              </Routes>
            </RequireAuth>
          </TooltipProvider>
        </LayoutStyleProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
